import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { listSuspensionHistory } from "@/lib/incidents.functions";
import { ReserveHeader } from "@/components/ReserveHeader";

export const Route = createFileRoute("/_authenticated/admin/suspension-history/$userId")({
  head: () => ({ meta: [{ title: "Suspension History — Admin Console" }] }),
  component: SuspensionHistoryPage,
});

type Profile = { id: string; full_name: string | null; role: string | null; is_suspended: boolean | null; suspended_at: string | null; suspension_reason: string | null } | null;
type Event = { id: string; action: "suspended" | "lifted"; reason: string | null; admin_id: string | null; admin_name: string | null; created_at: string };
type Spell = { suspended_at: string; lifted_at: string | null; reason: string | null; admin_id: string | null; admin_name: string | null; duration_ms: number | null };

function formatDuration(ms: number | null): string {
  if (ms === null) return "ongoing";
  const mins = Math.max(0, Math.floor(ms / 60000));
  if (mins < 60) return `${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ${mins % 60}m`;
  const days = Math.floor(hrs / 24);
  return `${days}d ${hrs % 24}h`;
}

function SuspensionHistoryPage() {
  const { userId } = Route.useParams();
  const load = useServerFn(listSuspensionHistory);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<Profile>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [spells, setSpells] = useState<Spell[]>([]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await load({ data: { user_id: userId } });
        setProfile(r.profile as Profile);
        setEvents(r.events as Event[]);
        setSpells(r.spells as Spell[]);
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Could not load suspension history.");
      } finally {
        setLoading(false);
      }
    })();
  }, [userId]);

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader role="admin" />
      <main className="mx-auto max-w-4xl px-6 py-10">
        <Link to="/admin/suspensions" className="text-xs tracking-wide-luxury text-accent hover:text-accent/80">
          ← Back to suspensions
        </Link>
        <p className="mt-4 tracking-wide-luxury text-accent">Admin Console</p>
        <h1 className="mt-3 font-display text-3xl">Suspension History</h1>
        <span className="gold-divider mt-4" />

        {loading ? (
          <p className="mt-12 text-center text-sm text-muted-foreground">Loading…</p>
        ) : !profile ? (
          <p className="mt-12 text-center text-sm text-muted-foreground">User not found.</p>
        ) : (
          <>
            <section className="mt-8 border border-border bg-card p-5">
              <p className="font-display text-xl">{profile.full_name || "Unnamed"}</p>
              <p className="mt-1 text-xs text-muted-foreground capitalize">{profile.role || "—"}</p>
              <p className="mt-3 text-sm">
                Current status:{" "}
                <span className={profile.is_suspended ? "text-destructive" : "text-accent"}>
                  {profile.is_suspended ? "Suspended" : "Active"}
                </span>
              </p>
            </section>

            <section className="mt-8">
              <h2 className="font-display text-2xl">Suspension spells</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Every period during which this account was suspended, oldest at the bottom.
              </p>
              {spells.length === 0 ? (
                <p className="mt-6 text-sm text-muted-foreground">No prior suspensions on record.</p>
              ) : (
                <div className="mt-6 overflow-x-auto border border-border bg-card">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/30 text-left text-xs tracking-wide-luxury text-muted-foreground">
                      <tr>
                        <th className="px-4 py-3">Suspended</th>
                        <th className="px-4 py-3">Lifted</th>
                        <th className="px-4 py-3">Duration</th>
                        <th className="px-4 py-3">Reason</th>
                        <th className="px-4 py-3">By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {spells.map((s, i) => (
                        <tr key={i} className="border-t border-border align-top">
                          <td className="px-4 py-3 whitespace-nowrap">{new Date(s.suspended_at).toLocaleString()}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {s.lifted_at ? new Date(s.lifted_at).toLocaleString() : <span className="text-destructive">ongoing</span>}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">{formatDuration(s.duration_ms)}</td>
                          <td className="px-4 py-3">{s.reason || <span className="text-muted-foreground">—</span>}</td>
                          <td className="px-4 py-3">{s.admin_name || <span className="text-muted-foreground">{s.admin_id?.slice(0, 8) || "system"}</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="mt-10">
              <h2 className="font-display text-2xl">Full audit log</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Every suspend and lift action recorded against this account.
              </p>
              {events.length === 0 ? (
                <p className="mt-6 text-sm text-muted-foreground">No audit entries.</p>
              ) : (
                <ol className="mt-6 space-y-3">
                  {[...events].reverse().map((e) => (
                    <li key={e.id} className="border border-border bg-card p-4">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <p className="font-display text-base">
                          <span className={e.action === "suspended" ? "text-destructive" : "text-accent"}>
                            {e.action === "suspended" ? "Suspended" : "Lifted"}
                          </span>{" "}
                          <span className="text-xs text-muted-foreground tracking-wide-luxury">
                            by {e.admin_name || e.admin_id?.slice(0, 8) || "system"}
                          </span>
                        </p>
                        <p className="text-xs text-muted-foreground">{new Date(e.created_at).toLocaleString()}</p>
                      </div>
                      {e.reason && <p className="mt-2 text-sm">{e.reason}</p>}
                    </li>
                  ))}
                </ol>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
